import { useCreateTask, useWorkflow, useWorkflows } from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/dialog";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { LoadingSpinner } from "@/components/loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { useToast } from "@/components/use-toast";
import { DOCUMENT_GROUP_NODE_NAME } from "@repo/shared/migration-types";
import {
  type DocumentNode,
  isDocumentNode,
  isGroupNode,
} from "@repo/shared/workflow";
import _ from "lodash";
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

export const CreateTaskDialog = () => {
  const { data: workflowsResp } = useWorkflows();

  const workflows = workflowsResp?.items ?? [];

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<
    string | undefined
  >(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const firstWorkflow = workflows?.[0];
    if (firstWorkflow && !selectedWorkflowId) {
      setSelectedWorkflowId(firstWorkflow.id);
    }
  }, [workflows, selectedWorkflowId]);

  const handleSubmit = () => {
    setOpen(false);
  };

  const selectedWorkflow = workflows?.find((w) => w.id === selectedWorkflowId);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default">Create Task</Button>
      </DialogTrigger>
      <DialogContent className="max-w-[calc(min(70dvw,800px))]">
        <DialogHeader>
          <DialogTitle>Create Task</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Label>Project</Label>
          <Select
            value={selectedWorkflowId?.toString()}
            onValueChange={(value) => setSelectedWorkflowId(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {workflows?.map((workflow) => (
                <SelectItem key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedWorkflowId && selectedWorkflow?.currentVersion.id && (
            <Form
              workflowId={selectedWorkflowId}
              workflowVersionId={selectedWorkflow?.currentVersion.id}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type FileRow = {
  file: File | null;
  docNodeId: string;
  required: boolean;
};

const Form = ({
  workflowId,
  workflowVersionId,
  onSubmit,
}: {
  workflowId: string;
  workflowVersionId: string;
  onSubmit: () => void;
}) => {
  const { data: workflowBody } = useWorkflow(workflowId);
  const workflow = workflowBody?.workflow;
  const createTaskMut = useCreateTask();

  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [fileRows, setFileRows] = useState<FileRow[]>([]);
  const [taskId, setTaskId] = useState<string>("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset the form when the workflow changes
  useEffect(() => {
    setFileRows([]);
    setTaskId("");
  }, [workflowId]);

  const documentNodes = useMemo(() => {
    return workflow?.nodes.filter(isDocumentNode) as DocumentNode[];
  }, [workflow]);

  useEffect(() => {
    // Initialize file rows based on available document nodes
    const documentGroupNode = workflow?.nodes.find(
      (n) => isGroupNode(n) && n.name === DOCUMENT_GROUP_NODE_NAME,
    );
    const relevantNodes = documentGroupNode?.dependencyIds.map((id) =>
      workflow?.nodes.find((n) => n.id === id),
    ) as DocumentNode[];
    if (relevantNodes) {
      setFileRows(
        relevantNodes.map((node) => ({
          docNodeId: node.id,
          file: null,
          required: node.required,
        })),
      );
    }
  }, [workflow]);

  useEffect(() => {
    // Try to infer the report number from any of the filenames, if it's not already set.
    if (taskId === "" && fileRows.length > 0) {
      for (const row of fileRows) {
        if (row.file) {
          const reportNumber = row.file.name.match(/(\d+)/)?.[0];
          if (reportNumber) {
            setTaskId(reportNumber);
            return;
          }
        }
      }
    }
  }, [fileRows, taskId]);

  const handleFileChange = (index: number, file: File | null) => {
    const newRows = [...fileRows];
    newRows[index].file = file;
    setFileRows(newRows);
  };

  const handleSubmit = async (e: FormEvent) => {
    setSubmitting(true);
    e.preventDefault();

    if (taskId.trim() !== taskId) {
      toast({
        title: "Error",
        description: "Task ID cannot contain leading or trailing spaces",
        variant: "destructive",
        duration: 2000,
      });
      setSubmitting(false);
      e.preventDefault();
      return;
    }

    const missingFiles = fileRows.filter((row) => !row.file && row.required);
    if (missingFiles.length > 0) {
      const missingTypes = documentNodes
        ?.filter((e) => missingFiles.some((row) => row.docNodeId === e.id))
        .map((e) => e.name);

      toast({
        title: "Error",
        description: `Please select all required files. Missing: ${missingTypes?.join(", ")}`,
        variant: "destructive",
        duration: 2000,
      });
      setSubmitting(false);
      return;
    }

    try {
      const fileMap = fileRows.reduce<Record<string, File>>((acc, row) => {
        if (row.file) {
          acc[row.docNodeId] = row.file;
        }
        return acc;
      }, {});

      await createTaskMut.mutateAsync({
        taskId,
        workflowVersionId,
        files: fileMap,
      });

      toast({
        title: "Documents uploaded successfully",
        variant: "default",
        duration: 2000,
      });

      onSubmit();
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast({
        title: "Error uploading documents",
        description: "Please try again",
        variant: "destructive",
        duration: 2000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div key={workflowId}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4 py-4">
          <Label>Upload files</Label>
          <div className="border rounded-md divide-y striped">
            {fileRows.map((row, index) => {
              const documentNode = documentNodes?.find(
                (e) => e.id === row.docNodeId,
              );

              if (!documentNode) return null;

              return (
                <div
                  key={row.docNodeId}
                  className="grid items-center pl-4 pr-2 py-2 grid-cols-2 gap-2"
                >
                  <div>
                    {documentNode.name}
                    {documentNode.required && (
                      <span className="text-sm text-red-500">*</span>
                    )}
                    <span className="text-sm text-gray-500 ml-2">
                      {documentNode.documentType !== "pdf"
                        ? documentNode.documentType
                        : ""}
                    </span>
                  </div>
                  <Input
                    type="file"
                    className="bg-white hover:bg-blue-50 cursor-pointer"
                    accept={(() => {
                      switch (documentNode.documentType) {
                        case "pdf":
                          return ".pdf,application/pdf";
                        case "xlsx":
                          return ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                        case "csv":
                          return ".csv,text/csv";
                        default:
                          return "";
                      }
                    })()}
                    onChange={(e) =>
                      handleFileChange(index, e.target.files?.[0] || null)
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="h-2" />
          <Label>Task ID</Label>
          <Input
            type="text"
            placeholder="Enter task ID"
            value={taskId}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setTaskId(e.target.value)
            }
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting && (
              <div className="mr-2 mt-0.5">
                <LoadingSpinner size="small" />
              </div>
            )}
            <p>Create Task</p>
          </Button>
        </div>
      </form>
    </div>
  );
};
